import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { publicSettingsAPI } from '../services/api';
import { DEFAULT_THEME, isValidTheme, getThemeMeta, THEME_IDS } from './registry';

const ThemeContext = createContext(null);

const CACHE_KEY = 'niswa_active_theme';
const PREVIEW_KEY = 'niswa_theme_preview'; // admin/local instant override
const T2_MODE_KEY = 'niswa_theme2_mode'; // light | dark (theme2 only)

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
};

const readCachedTheme = () => {
  try {
    // A local preview (set by admin) always wins for instant switching.
    const preview = localStorage.getItem(PREVIEW_KEY);
    if (preview && isValidTheme(preview)) return preview;
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached && isValidTheme(cached)) return cached;
  } catch (e) {
    /* ignore */
  }
  return DEFAULT_THEME;
};

const readT2Mode = () => {
  try {
    const saved = localStorage.getItem(T2_MODE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
    // Fall back to the existing global dark-mode preference, then system.
    const globalTheme = localStorage.getItem('theme');
    if (globalTheme === 'light' || globalTheme === 'dark') return globalTheme;
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch (e) {
    /* ignore */
  }
  return 'light';
};

export const ThemeProvider = ({ children }) => {
  // Start from cache so the correct theme paints instantly (no flash).
  const [activeTheme, setActiveTheme] = useState(readCachedTheme);
  const [themeLoaded, setThemeLoaded] = useState(false);
  const [t2Mode, setT2Mode] = useState(readT2Mode);

  // Fetch the canonical active theme from the database (global source of truth).
  const fetchActiveTheme = useCallback(async () => {
    try {
      const response = await publicSettingsAPI.getSettings();
      const settings = response?.data?.data?.settings;
      const value = settings?.active_theme?.value;
      if (value && isValidTheme(value)) {
        // Only override the cached value if there is no explicit local preview.
        const preview = localStorage.getItem(PREVIEW_KEY);
        if (!preview) {
          setActiveTheme(value);
          try { localStorage.setItem(CACHE_KEY, value); } catch (e) { /* ignore */ }
        }
      }
    } catch (err) {
      // Network/API failure -> keep cached/default theme. Site keeps working.
      console.warn('Theme: falling back to cached/default theme.', err?.message);
    } finally {
      setThemeLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchActiveTheme();
  }, [fetchActiveTheme]);

  // Keep the document flagged with the active theme (useful for global CSS scoping).
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', activeTheme);
    return () => {};
  }, [activeTheme]);

  // Theme 2 light/dark mode -> reuse the existing `dark` class strategy so all
  // Tailwind `dark:` utilities (used across existing pages) keep working.
  useEffect(() => {
    if (activeTheme !== THEME_IDS.THEME2) return;
    const root = document.documentElement;
    if (t2Mode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    try {
      localStorage.setItem(T2_MODE_KEY, t2Mode);
      // Keep the shared preference in sync so Theme 1 stays consistent too.
      localStorage.setItem('theme', t2Mode);
    } catch (e) { /* ignore */ }
  }, [t2Mode, activeTheme]);

  // Cross-tab + same-tab sync: when admin switches theme we broadcast it.
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === PREVIEW_KEY || e.key === CACHE_KEY) {
        setActiveTheme(readCachedTheme());
      }
      if (e.key === T2_MODE_KEY) {
        setT2Mode(readT2Mode());
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Instant local switch (used by admin preview + after a successful save).
  const applyTheme = useCallback((id, { persistLocal = true } = {}) => {
    if (!isValidTheme(id)) return;
    setActiveTheme(id);
    try {
      localStorage.setItem(CACHE_KEY, id);
      if (persistLocal) {
        localStorage.setItem(PREVIEW_KEY, id);
      }
    } catch (e) { /* ignore */ }
  }, []);

  const clearPreview = useCallback(() => {
    try { localStorage.removeItem(PREVIEW_KEY); } catch (e) { /* ignore */ }
    fetchActiveTheme();
  }, [fetchActiveTheme]);

  const toggleT2Mode = useCallback(() => {
    setT2Mode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo(
    () => ({
      activeTheme,
      themeLoaded,
      meta: getThemeMeta(activeTheme),
      isTheme2: activeTheme === THEME_IDS.THEME2,
      applyTheme,
      clearPreview,
      refreshTheme: fetchActiveTheme,
      // Theme 2 light/dark
      t2Mode,
      isDark: t2Mode === 'dark',
      setT2Mode,
      toggleT2Mode,
    }),
    [activeTheme, themeLoaded, applyTheme, clearPreview, fetchActiveTheme, t2Mode, toggleT2Mode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeProvider;
